import type { CostIndexClimber, CostIndexSkuClimber, Currency } from "./types";
import { currencySymbol, money, pctPoints } from "./format";
import { formatClimberCash, formatClimberDelta, formatClimberPct, formatClimberUnits } from "./climberFormat";
import type { ImpactRule, MatrixKind, MatrixPoint, MatrixRow, MatrixSplits, MatrixSummary, RiseKind } from "./focusMatrix";
import { monthShort } from "./palette";

type Tile = { label: string; value: string; note?: string };
type InflationSummary = { series: string; pct: number };

const signedMoney = (value: number, currency: Currency | null, places = 0) => currency
  ? money(value, currency, places)
  : "–";

function summaryTiles(result: MatrixSummary, split: MatrixSplits, kind: MatrixKind, currency: Currency | null, unitsUnavailable: boolean, inflation: InflationSummary | null | undefined, symbol: string, riseKind?: RiseKind, impactKind?: ImpactRule["kind"]): Tile[] {
  const noun = kind === "skus" ? "SKUs" : kind === "places" ? "places" : kind === "lanes" ? "lanes" : "cost lines";
  const tiles: Tile[] = [
    { label: "In the focus corner", value: `${result.cornerCount} of ${result.plotted}`, note: noun },
    { label: `${symbol} at stake`, value: signedMoney(result.cornerImpact, currency) },
    { label: "Work these first", value: result.cornerNames.join(", ") || "none" },
    inflation
      ? { label: "CPI over the window", value: pctPoints(inflation.pct, 1), note: inflation.series }
      : riseKind === "pct"
        ? { label: "Rise line you set", value: pctPoints(split.y, 1) }
        : riseKind === "none"
          ? { label: "No rise reference", value: pctPoints(split.y, 1) }
          : { label: "Scope average", value: pctPoints(split.y, 1) },
    impactKind === "amount"
      ? { label: "Impact line you set", value: signedMoney(split.x, currency) }
      : { label: "Mean impact", value: signedMoney(split.x, currency) },
  ];
  if (result.notPlotted > 0) tiles.push({
    label: "Not plotted",
    value: `${result.notPlotted} not plotted: ${unitsUnavailable ? "units unavailable" : `no ${symbol} value`}`,
  });
  return tiles;
}

const position = (point: MatrixPoint, split: MatrixSplits, inflation?: InflationSummary | null) => {
  if (!inflation) return point.x >= split.x && point.y > split.y ? "in the focus corner" : point.y > split.y ? "above the line" : "below the line";
  return point.x >= split.x && point.y > split.y ? "in the focus corner" : point.y > split.y ? "above inflation" : "within inflation";
};

const deltaLabel = (inflation?: InflationSummary | null) => inflation ? `Δ % vs CPI ${pctPoints(inflation.pct, 0)}` : "Δ %";

function skuTiles(point: MatrixPoint<CostIndexSkuClimber>, split: MatrixSplits, currency: Currency | null, unitsUnavailable: boolean, inflation: InflationSummary | null | undefined, symbol: string): Tile[] {
  const row = point.row;
  const carried = row.carried_since ? ` · carried since ${monthShort(row.carried_since)}` : "";
  return [
    { label: `${row.sku}${carried}`, value: `${row.tracking} · ${row.variant}`, note: row.region },
    { label: `Start → latest ${symbol}/unit`, value: `${formatClimberCash(row.base_unit_cost, currency)} → ${formatClimberCash(row.latest_unit_cost, currency)}` },
    { label: `Δ ${symbol} per unit`, value: formatClimberDelta(row.delta_abs, currency) },
    { label: deltaLabel(inflation), value: formatClimberPct(row.delta_pct), note: position(point, split, inflation) },
    { label: "Units (window)", value: formatClimberUnits(row.units_sold_window, unitsUnavailable) },
    { label: "Impact (window)", value: unitsUnavailable ? "–" : formatClimberCash(row.impact_window, currency, 0) },
    { label: "Biggest contributor", value: row.top_contributing_line?.label ?? "–" },
  ];
}

function lineTiles(point: MatrixPoint<CostIndexClimber>, split: MatrixSplits, currency: Currency | null, unitsUnavailable: boolean, inflation: InflationSummary | null | undefined, symbol: string): Tile[] {
  const row = point.row;
  return [
    { label: row.label, value: row.kind === "material" ? "Material" : "Account" },
    { label: `Start → latest ${symbol}/unit`, value: `${formatClimberCash(row.base_unit_cost, currency, 3)} → ${formatClimberCash(row.latest_unit_cost, currency, 3)}` },
    { label: `Δ ${symbol}`, value: formatClimberDelta(row.delta_abs, currency, 3) },
    { label: deltaLabel(inflation), value: formatClimberPct(row.delta_pct), note: inflation ? position(point, split, inflation) : undefined },
    { label: "Units (window)", value: formatClimberUnits(row.units_sold_window, unitsUnavailable) },
    { label: "Impact (window)", value: unitsUnavailable ? "–" : formatClimberCash(row.impact_window, currency, 0) },
  ];
}

function placeTiles(point: MatrixPoint<MatrixRow>, split: MatrixSplits, currency: Currency | null, unitsUnavailable: boolean, inflation?: InflationSummary | null): Tile[] {
  const row = point.row as MatrixRow & { label: string; currency?: Currency };
  const rowCurrency = row.currency ?? currency;
  return [
    { label: row.label, value: position(point, split, inflation) },
    { label: deltaLabel(inflation), value: formatClimberPct(row.delta_pct == null ? null : String(row.delta_pct)) },
    { label: "Impact (window)", value: unitsUnavailable ? "–" : formatClimberCash(row.impact_window == null ? null : String(row.impact_window), rowCurrency, 0) },
  ];
}

export function MatrixStats({ kind, result, split, active, currency, unitsUnavailable, inflation, labelCurrency = "GBP", riseKind, impactKind }: {
  kind: MatrixKind;
  result: MatrixSummary;
  split: MatrixSplits;
  active: MatrixPoint | null;
  currency: Currency | null;
  unitsUnavailable: boolean;
  inflation?: InflationSummary | null;
  /** The currency the tile labels name ("£ at stake"); GBP unless the caller presents another currency. */
  labelCurrency?: Currency;
  riseKind?: RiseKind;
  impactKind?: ImpactRule["kind"];
}) {
  const symbol = currencySymbol(labelCurrency);
  const tiles = active
    ? kind === "skus"
      ? skuTiles(active as MatrixPoint<CostIndexSkuClimber>, split, currency, unitsUnavailable, inflation, symbol)
      : kind === "places" || kind === "lanes"
        ? placeTiles(active, split, currency, unitsUnavailable, inflation)
        : lineTiles(active as MatrixPoint<CostIndexClimber>, split, currency, unitsUnavailable, inflation, symbol)
    : summaryTiles(result, split, kind, currency, unitsUnavailable, inflation, symbol, riseKind, impactKind);

  return <dl data-matrix-stats className="sticky bottom-0 z-10 grid grid-flow-col auto-cols-fr overflow-hidden rounded-md border border-border bg-card">
    {tiles.slice(0, 7).map((tile) => {
      const title = [tile.label, tile.value, tile.note].filter(Boolean).join(": ");
      return <div key={tile.label} title={title} className="min-w-0 border-r border-border px-3 py-2 text-center last:border-r-0">
        <dt className="truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{tile.label}</dt>
        <dd className="truncate text-sm font-semibold text-ks-ink">{tile.value}</dd>
        {tile.note && <dd className="truncate text-[11px] text-muted-foreground">{tile.note}</dd>}
      </div>;
    })}
  </dl>;
}
